package com.meistercharts.charts.pv.stringsPlanning

import com.meistercharts.charts.pv.roofPlanning.Module
import com.meistercharts.charts.pv.roofPlanning.ModulePlacement
import it.neckar.open.observable.ObservableList
import kotlin.uuid.Uuid

/**
 * Contains the [ModulePlacement]s
 */
class RoofString(
  val uuid: Uuid,
  val moduleLayoutUuid: Uuid,
  val inverterIndex: Int,
  val mpptInputIndex: Int,
  val stringIndex: StringIndex,
  val previousModulesCount: Int,
  val followingModulesCount: Int,
  val stringColorIndexOverride: StringIndex? = null,
  initialModules: List<Module> = emptyList()
) {
  /**
   * The [RoofString] property
   */
  val modulesProperty: ObservableList<Module> = ObservableList(initialModules)

  /**
   * Getter for the current [Module]
   */
  val modules: List<Module> by modulesProperty

  /**
   * Returns the [Module] count
   */
  val count: Int
    get() = modules.size

  val globalStringIndex: StringIndex
    get() = stringColorIndexOverride ?: stringIndex

  fun clear() {
    modulesProperty.value = emptyList()
  }

  fun add(module: Module) {
    modulesProperty.getAndSet {
      it.plus(module)
    }
  }

  fun addAll(module: Iterable<Module>) {
    modulesProperty.getAndSet {
      it.plus(module)
    }
  }

  /**
   * Adds the module back at the position it was removed from. The order of the modules is the wiring order, so undoing
   * a removal has to restore the position, not just the membership.
   */
  fun insert(index: Int, module: Module) {
    modulesProperty.getAndSet {
      it.toMutableList().apply {
        add(index.coerceIn(0, size), module)
      }
    }
  }

  fun contains(module: Module): Boolean {
    return modules.contains(module)
  }

  /**
   * Sets the unusableAreas
   */
  fun set(module: Iterable<Module>) {
    modulesProperty.value = module.toMutableList()
  }

  operator fun get(index: Int): Module {
    return modules[index]
  }

  /**
   * Removes the given [Module]
   */
  fun removeAll(toRemove: Set<Module>) {
    removeAll {
      toRemove.contains(it)
    }
  }

  fun removeAll(predicate: (Module) -> Boolean) {
    modulesProperty.getAndSet {
      it.toMutableList().apply {
        removeAll(predicate)
      }
    }
  }

  fun remove(module: Module) {
    modulesProperty.getAndSet {
      it.minus(module)
    }
  }

  override fun toString(): String {
    return "RoofString(uuid=$uuid, moduleLayoutUuid=$moduleLayoutUuid, modules=$modules)"
  }

}
